import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/vue-query';
import { computed, toValue, watch, type MaybeRefOrGetter } from 'vue';
import { AxiosError } from 'axios';
import { toast } from 'vue-sonner';
import http from '@/services/http';
import type { SavedViewRecord } from '@/utils/savedViews';
import type { SavedViewDefinitionV2 } from '@/utils/savedViewDefinition';

/**
 * API layer for a user's own saved views (SAU-310). Per-user rows in the
 * backend `saved_views` table, scoped per entity/page by the same key the
 * persistent table state uses.
 *
 * The optimistic cache choreography lives HERE, in the mutations (onMutate
 * patches, onError rolls back surgically, onSettled reconciles) — the same
 * shape as useFavoriteFiltersApi. The mutation variables carry the entity, so
 * a mid-flight page switch can never make a mutation settle against the wrong
 * cache key.
 */

const RESOURCE_BASE = '/api/saved_view';

/** Prefix shared by every entity's saved-views key, for invalidating them all at once. */
export const SAVED_VIEWS_QUERY_PREFIX = ['savedViews'] as const;

/**
 * The key for ONE entity's list under ONE search term.
 *
 * The term is part of the key rather than a refetch trigger, so clearing the box
 * shows the unfiltered list from cache instead of going back to the network — the
 * same choice the library dialog already made.
 *
 * Every mutation invalidates the PREFIX below rather than this exact key, because a
 * save made while a term is typed also changes the unfiltered list, and the list the
 * user sees next is usually the one they did not have on screen.
 */
export const savedViewsQueryKey = (entityId: string, search: string) =>
  ['savedViews', entityId, search] as const;

/** Every cached term for one entity — what a mutation invalidates. */
export const savedViewsEntityKey = (entityId: string) => ['savedViews', entityId] as const;

/**
 * The entity the section sidebar asks for: none. The list endpoint filters by
 * entity only when one is named, so this returns every view the user owns, and
 * the sidebar sorts them into areas itself.
 */
export const ALL_ENTITIES = '';

/**
 * One view held on its own, for {@link useSavedViewQuery}.
 *
 * NOT under the `savedViews` prefix, and that is why every mutation has to name it
 * explicitly. `savedViews` and `savedView` differ by one character and do not
 * prefix-match, so invalidating the listing leaves this copy untouched — which made
 * the row the by-id fallback exists to keep correct the one row that went stale and
 * stayed stale. Whenever a mutation changes a view, it must touch BOTH.
 */
export const savedViewByIdQueryKey = (savedViewId: number) =>
  ['savedView', savedViewId] as const;

/** Rows per request. The server clamps anything larger; this just has to agree. */
const PAGE_SIZE = 100;

/**
 * One page of the listing, exactly as `SavedViewController::index` returns it.
 *
 * NO `total`. The menu pages with "Load more", which needs the next cursor and
 * nothing else, and the endpoint deliberately does not count — every first-page
 * request would otherwise pay for a COUNT under a leading-wildcard LIKE, once per
 * debounced keystroke in the search box, for a number nothing renders. The shared
 * library listing does send one, because its dialog shows "Page 2 of 5".
 */
export interface SavedViewPage {
  data: SavedViewRecord[];
  meta?: {
    per_page: number;
    next_cursor: string | null;
    prev_cursor: string | null;
  };
}

/** The cache shape TanStack keeps for an infinite query. */
export interface SavedViewPages {
  pages: SavedViewPage[];
  pageParams: unknown[];
}

/**
 * Rewrite the rows of every loaded page.
 *
 * The optimistic patches used to be plain array operations over one flat list. Now
 * that the listing is paged the cache holds a page ARRAY, and a row being patched or
 * removed may sit on any of the loaded pages — so every one is rewritten rather than
 * just the first. Written as a functional updater throughout, because
 * `setQueryData(key, undefined)` is a no-op in TanStack Query v5: a snapshot-style
 * rollback would silently do nothing whenever the query was never populated.
 */
function rewriteLoadedPages(
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  rewrite: (rows: SavedViewRecord[]) => SavedViewRecord[],
): void {
  queryClient.setQueryData<SavedViewPages>(queryKey, (old) =>
    old ? { ...old, pages: old.pages.map((page) => ({ ...page, data: rewrite(page.data) })) } : old,
  );
}

/**
 * Patch the list of every view, which the sidebar, the breadcrumb and Cmd+K
 * read, after a change succeeded. It used to be invalidated instead, and that
 * read every page of every view again, one request after another, after each
 * save, rename and delete. It is only patched when it is loaded: patching an
 * empty cache would make one row look like the whole list.
 */
function patchAllViews(queryClient: QueryClient, rewrite: (rows: SavedViewRecord[]) => SavedViewRecord[], addFirst?: SavedViewRecord): void {
  queryClient.setQueryData<SavedViewPages>(savedViewsQueryKey(ALL_ENTITIES, ''), (old) => {
    if (!old) return old;
    const pages = old.pages.map((page) => ({ ...page, data: rewrite(page.data) }));
    if (addFirst && pages.length > 0) {
      pages[0] = { ...pages[0], data: [addFirst, ...pages[0].data] };
    }
    return { ...old, pages };
  });
}

/** After a change failed, read the whole list again, so it matches the server. */
function refreshAllViews(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: savedViewsEntityKey(ALL_ENTITIES) });
}

/**
 * Put a row at the top of the first loaded page.
 *
 * The listing is newest-first and an optimistic row is the newest there is, so the
 * top of page one is where the server will put it too — the optimistic position is
 * the real one, not an approximation that jumps on the next refetch.
 */
function addToFirstLoadedPage(
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  row: SavedViewRecord,
): void {
  queryClient.setQueryData<SavedViewPages>(queryKey, (old) => {
    // Never populated — the backend 404s before its deploy, or the user saved from a
    // toolbar whose menu they have not opened yet. Synthesised as the single page the
    // first fetch would have produced, so the row is visible and the rollback below
    // still has something to remove it from.
    if (!old) {
      return { pages: [{ data: [row] }], pageParams: [null] };
    }
    return {
      ...old,
      pages: old.pages.map((page, index) =>
        index === 0 ? { ...page, data: [row, ...page.data] } : page,
      ),
    };
  });
}

/** Every row the client currently holds for a list, flattened across its pages. */
export function flattenSavedViewPages(pages: SavedViewPages | undefined): SavedViewRecord[] {
  return pages?.pages.flatMap((page) => page.data ?? []) ?? [];
}

/** Placeholder ids for optimistic rows — negative so they never collide with real DB ids. */
let nextOptimisticSavedViewId = -1;

/**
 * True for the placeholder id of an optimistic row whose POST has not resolved.
 * Such a row must never be PUT or DELETEd (the id does not exist server-side);
 * the UI shows it as pending instead.
 */
export function isOptimisticSavedViewId(savedViewId: number): boolean {
  return savedViewId < 0;
}

/**
 * The list a mutation should patch optimistically: its entity AND the search term
 * that was in the box when the user acted.
 *
 * Both are captured in the mutation variables rather than read at settle time, so a
 * page switch or a keystroke while the request is in flight cannot make the patch
 * land on a different list than the one the user was looking at.
 */
interface SavedViewListScope {
  entity: string;
  /** '' for the unfiltered list. */
  search: string;
}

export interface SavedViewSaveInput extends SavedViewListScope {
  name: string;
  definition: SavedViewDefinitionV2;
}

export interface SavedViewUpdateInput extends SavedViewListScope {
  savedViewId: number;
  name: string;
  definition: SavedViewDefinitionV2;
}

export interface SavedViewDeleteInput extends SavedViewListScope {
  savedViewId: number;
}

interface ItemEnvelope<T> {
  data: T;
}

/**
 * The server's own reason when it refused a save, or null for any other failure.
 *
 * Two statuses carry one. A 409 is `SavedViewController::store` refusing a name
 * it already holds — it decides that with the unique index, which folds accents
 * and trailing spaces the way no client-side check reproduces, so the message is
 * information the user does not otherwise have. A 422 is validation, and the
 * first message from ANY field is surfaced: reading only `errors.name` collapsed
 * "You do not have access to AudioFileIssue" into a bare "Could not save the
 * view", which the user then retried, and retried.
 */
export function serverRefusalMessage(error: unknown): string | null {
  if (!(error instanceof AxiosError)) {
    return null;
  }
  const status = error.response?.status;
  if (status !== 409 && status !== 422) {
    return null;
  }
  const data = error.response?.data as
    | { message?: string; errors?: Record<string, string[]> }
    | undefined;
  const firstField = Object.values(data?.errors ?? {}).find((messages) => messages.length > 0);
  return firstField?.[0] ?? data?.message ?? null;
}

export function useSavedViewsQuery(
  entityId: MaybeRefOrGetter<string>,
  search: MaybeRefOrGetter<string>,
) {
  // NOT fetched until there is an entity to fetch for. `entityStore` documents
  // that `selectedEntityId` can start empty, because permissions arrive with
  // /api/user after the store is set up — and `SavedViewController::index`
  // treats an absent entity as NO FILTER, so a request sent in that window
  // comes back with every entity's views at once. The menu would then list
  // another page's views, and loading one would expand its definition against
  // the wrong column universe and write a blank layout. The section sidebar
  // wants exactly that unfiltered listing, and asks for it on purpose through
  // {@link useAllSavedViewsQuery}.
  return savedViewsInfiniteQuery(entityId, search, computed(() => toValue(entityId) !== ''));
}

/**
 * Every view the user owns, across every entity and every page, for the section
 * sidebar, which sorts them into areas itself. Cached under {@link ALL_ENTITIES},
 * which every mutation invalidates.
 */
export function useAllSavedViewsQuery() {
  const query = savedViewsInfiniteQuery(ALL_ENTITIES, '', true);
  // The sidebar lists every view, not a first page, so keep reading until the
  // server runs out. The short-page guard in getNextPageParam ends the loop.
  watch(
    () => [query.hasNextPage.value, query.isFetchingNextPage.value] as const,
    ([hasNextPage, isFetchingNextPage]) => {
      if (hasNextPage && !isFetchingNextPage) {
        void query.fetchNextPage();
      }
    },
    { immediate: true },
  );
  return query;
}

function savedViewsInfiniteQuery(
  entityId: MaybeRefOrGetter<string>,
  search: MaybeRefOrGetter<string>,
  enabled: MaybeRefOrGetter<boolean>,
) {
  return useInfiniteQuery({
    queryKey: computed(() => savedViewsQueryKey(toValue(entityId), toValue(search))),
    initialPageParam: null as string | null,
    queryFn: async ({ queryKey: [, entity, term], pageParam }): Promise<SavedViewPage> => {
      const response = await http.get<SavedViewPage>(RESOURCE_BASE, {
        params: {
          // Left out, not sent empty, when the sidebar asks for every entity.
          ...(entity ? { entity } : {}),
          perPage: PAGE_SIZE,
          ...(term ? { search: term } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      });
      // NOT `?? []`. axios is built with responseType 'json' and no
      // `transitional`, so its default silentJSONParsing swallows a JSON.parse
      // failure and hands back the raw string — an HTML 200 from an SSO
      // interstitial, a captive portal, a WAF page or a redirect the XHR quietly
      // followed all arrive with `response.data` as a string. Defaulting to an
      // empty array turned every one of those into "you have no saved views" on a
      // SUCCESSFUL query.
      //
      // That is worse than an empty menu: useSavedViews starts the localStorage
      // migration on isSuccess, so a bogus 200 also read as "the server has
      // nothing", and the migration uploaded and then cleared the local copies.
      // The control's own loadFailed row — "'Could not load' and 'you have none'
      // must not look the same" — never rendered either. A bare array (a
      // withoutWrapping() resource) is refused for the same reason.
      const rows = response.data?.data;
      if (!Array.isArray(rows)) {
        throw new Error('The saved-views listing did not answer with a data array.');
      }

      return { data: rows, meta: response.data?.meta };
    },
    // Stop on the server's null token, and stop early on a short page. The short-page
    // guard is what keeps a bug on either side from becoming an infinite fetch loop:
    // a final full page that still carried a token would otherwise be followed
    // forever, one empty request per click.
    getNextPageParam: (lastPage: SavedViewPage) =>
      (lastPage.data?.length ?? 0) < PAGE_SIZE ? undefined : (lastPage.meta?.next_cursor ?? undefined),
    // Views only change through the mutations below, and each invalidates its
    // entity's key on settle, so the cached list cannot go stale on its own.
    // Every mounted useSavedViews is an observer, and the sidebar and page mount
    // several. Without this each one refires the same GET.
    staleTime: Infinity,
    // The backend may lag the frontend deploy. A 404 will not heal on retry, so
    // fail fast into the control's error state — and, critically, the
    // localStorage migration only runs on a SUCCESSFUL settle, so failing fast
    // here is what stops it destroying local views before the API exists.
    retry: false,
    enabled,
  });
}

/**
 * One saved view by id, for the row the menu cannot find in what it has loaded.
 *
 * The control remembers the view you are working in as a bare id. That was always
 * resolved out of the cached list, which worked while the list was every view you
 * owned — and stopped working the moment it became one page of them. The row you
 * opened first thing this morning is off the end of a list you have since added
 * eleven views to, or it is filtered out by whatever you have typed in the search
 * box, and either way the id no longer resolves.
 *
 * All of those land in the same place without this: the primary button falls back to
 * "Save", the dirty dot disappears, and pressing it creates a SECOND view instead of
 * updating the one you believe you are in.
 *
 * Only fetched when the id is genuinely missing from the loaded pages — pass
 * `enabled` false the moment the list has it — so the common case costs nothing.
 */
export function useSavedViewQuery(
  savedViewId: MaybeRefOrGetter<number | null>,
  enabled: MaybeRefOrGetter<boolean>,
) {
  return useQuery({
    queryKey: computed(() => ['savedView', toValue(savedViewId)] as const),
    queryFn: async ({ queryKey: [, id] }): Promise<SavedViewRecord | null> => {
      const response = await http.get<ItemEnvelope<SavedViewRecord>>(`${RESOURCE_BASE}/${id}`);
      // Absent is a real answer here (a deleted view), so null is allowed — but a
      // non-object body is the same swallowed-parse hazard as the listing above.
      const row = response.data?.data ?? null;
      if (row !== null && typeof row !== 'object') {
        throw new Error('The saved-view lookup did not answer with a view.');
      }

      return row;
    },
    // A deleted view 404s and an optimistic id has no server row at all; neither
    // heals on retry, and both simply mean "no active view" to the caller.
    enabled: computed(() => {
      const id = toValue(savedViewId);
      return toValue(enabled) && typeof id === 'number' && !isOptimisticSavedViewId(id);
    }),
    retry: false,
    staleTime: Infinity,
  });
}

export function useSaveSavedView() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: SavedViewSaveInput): Promise<SavedViewRecord> => {
      const response = await http.post<ItemEnvelope<SavedViewRecord>>(RESOURCE_BASE, input);
      const saved = response.data?.data;
      if (!saved) {
        throw new Error('Saved view create response is missing its data envelope.');
      }
      return saved;
    },
    /**
     * Optimistic add so the menu shows the new view immediately. Patched with
     * functional updaters throughout: `setQueryData(key, undefined)` is a no-op
     * in TanStack Query v5, so a snapshot-style rollback would strand the
     * phantom row whenever the query was never populated (a pre-deploy 404).
     */
    onMutate: async (input) => {
      const queryKey = savedViewsQueryKey(input.entity, input.search);
      await queryClient.cancelQueries({ queryKey });
      const optimistic: SavedViewRecord = {
        id: nextOptimisticSavedViewId--,
        entity: input.entity,
        name: input.name,
        definition: input.definition,
        forked_from_shared_saved_view_id: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      addToFirstLoadedPage(queryClient, queryKey, optimistic);
      return { queryKey, optimisticId: optimistic.id };
    },
    /**
     * Swap the placeholder for the row the server actually created, BEFORE
     * onSettled's invalidation fires — so the refetch RECONCILES the list
     * instead of being the only path to a correct one.
     *
     * Without this, a POST that succeeds and a refetch that then fails leaves a
     * negative-id row in the cache. The list is `retry: false`, so one failed
     * attempt is enough, and `staleTime: Infinity` means nothing refreshes it
     * while the control stays mounted. The user is told "Saved view X", sees the
     * row, and finds its rename, delete and share buttons dead — every one of
     * them is gated on `isOptimisticSavedViewId`, which is exactly right for a
     * row whose id does not exist server-side, and exactly wrong here because
     * the row does exist.
     */
    onSuccess: (saved, _input, context) => {
      // Drop the placeholder AND any existing copy of the returned row from every
      // loaded page, then add the row back once. Two cases need the second half: a
      // refetch that landed first has already added the real row, and — the one that
      // bit — the server ABSORBING a save as a repeat of an existing view returns
      // that existing row, which is already in the list under its old name. A plain
      // placeholder swap left both, keyed by the same id. Position does not matter
      // to the reader: the menu sorts by name.
      rewriteLoadedPages(queryClient, context.queryKey, (rows) =>
        rows.filter((view) => view.id !== context.optimisticId && view.id !== saved.id),
      );
      addToFirstLoadedPage(queryClient, context.queryKey, saved);
    },
    onError: (error, _input, context) => {
      // Surgical rollback: drop exactly the row this mutation added, so a
      // concurrent mutation's optimistic patch is never clobbered.
      if (context) {
        rewriteLoadedPages(queryClient, context.queryKey, (rows) =>
          rows.filter((view) => view.id !== context.optimisticId),
        );
      }
      // The server refuses a name it already holds, and it decides that with the
      // unique index rather than with the browser's approximation of the collation
      // — so this is the one save failure the user can act on, and "Could not save
      // the view" would tell them nothing about why.
      toast.error(serverRefusalMessage(error) ?? 'Could not save the view.');
    },
    onSettled: (saved, error, input) => {
      // Keyed off the ENTITY THE MUTATION WAS ISSUED FOR, never the entity
      // active at resolve time — and off the entity PREFIX, so the term the user
      // had typed and the unfiltered list behind it are both refreshed.
      queryClient.invalidateQueries({ queryKey: savedViewsEntityKey(input.entity) });
      if (error || !saved) {
        refreshAllViews(queryClient);
      } else {
        // A repeat the server absorbed comes back as the existing row, so drop any copy first.
        patchAllViews(queryClient, (rows) => rows.filter((view) => view.id !== saved.id), saved);
      }
    },
  });
}

export function useUpdateSavedView() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: SavedViewUpdateInput): Promise<SavedViewRecord> => {
      const response = await http.put<ItemEnvelope<SavedViewRecord>>(
        `${RESOURCE_BASE}/${input.savedViewId}`,
        { entity: input.entity, name: input.name, definition: input.definition },
      );
      const updated = response.data?.data;
      if (!updated) {
        throw new Error('Saved view update response is missing its data envelope.');
      }
      return updated;
    },
    /** Optimistic in-place patch — see useSaveSavedView for the conventions. */
    onMutate: async (input) => {
      const queryKey = savedViewsQueryKey(input.entity, input.search);
      await queryClient.cancelQueries({ queryKey });
      const previous = flattenSavedViewPages(
        queryClient.getQueryData<SavedViewPages>(queryKey),
      ).find((view) => view.id === input.savedViewId);
      rewriteLoadedPages(queryClient, queryKey, (rows) =>
        rows.map((view) =>
          view.id === input.savedViewId
            ? { ...view, name: input.name, definition: input.definition }
            : view,
        ),
      );
      // The same patch on the standalone copy, when one is cached. Without it the
      // active view stays on its pre-update definition, so `isModified` keeps
      // reporting the change the user just saved and the dirty dot never clears.
      const byIdKey = savedViewByIdQueryKey(input.savedViewId);
      const previousById = queryClient.getQueryData<SavedViewRecord | null>(byIdKey);
      queryClient.setQueryData<SavedViewRecord | null>(byIdKey, (old) =>
        old ? { ...old, name: input.name, definition: input.definition } : old,
      );
      return { queryKey, previous, previousById, byIdKey };
    },
    onError: (error, _input, context) => {
      // Restore exactly the row we patched, addressed by id.
      const previous = context?.previous;
      if (context && previous) {
        rewriteLoadedPages(queryClient, context.queryKey, (rows) =>
          rows.map((view) => (view.id === previous.id ? previous : view)),
        );
      }
      if (context?.previousById) {
        queryClient.setQueryData(context.byIdKey, context.previousById);
      }
      toast.error(serverRefusalMessage(error) ?? 'Could not update the view.');
    },
    onSettled: (_data, error, input) => {
      queryClient.invalidateQueries({ queryKey: savedViewsEntityKey(input.entity) });
      if (error) {
        refreshAllViews(queryClient);
      } else {
        patchAllViews(queryClient, (rows) =>
          rows.map((view) => (view.id === input.savedViewId ? { ...view, name: input.name, definition: input.definition } : view)),
        );
      }
      // BOTH keys. `savedView` is not under the `savedViews` prefix, so the line
      // above does not reach the standalone copy of this row — see
      // savedViewByIdQueryKey for what that left stale.
      queryClient.invalidateQueries({ queryKey: savedViewByIdQueryKey(input.savedViewId) });
    },
  });
}

export function useDeleteSavedView() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ savedViewId }: SavedViewDeleteInput) => {
      await http.delete(`${RESOURCE_BASE}/${savedViewId}`);
      return savedViewId;
    },
    /** Optimistic removal — see useSaveSavedView for the conventions. */
    onMutate: async (input) => {
      const queryKey = savedViewsQueryKey(input.entity, input.search);
      await queryClient.cancelQueries({ queryKey });
      const removed = flattenSavedViewPages(
        queryClient.getQueryData<SavedViewPages>(queryKey),
      ).find((view) => view.id === input.savedViewId);
      rewriteLoadedPages(queryClient, queryKey, (rows) =>
        rows.filter((view) => view.id !== input.savedViewId),
      );
      return { queryKey, removed };
    },
    onError: (_error, _input, context) => {
      // Re-insert the exact row we removed, unless a refetch already restored
      // it; onSettled's invalidation re-establishes the server ordering.
      const removed = context?.removed;
      if (context && removed) {
        const present = flattenSavedViewPages(
          queryClient.getQueryData<SavedViewPages>(context.queryKey),
        ).some((view) => view.id === removed.id);
        if (!present) {
          addToFirstLoadedPage(queryClient, context.queryKey, removed);
        }
      }
      toast.error('Could not delete the view.');
    },
    onSettled: (_data, error, input) => {
      queryClient.invalidateQueries({ queryKey: savedViewsEntityKey(input.entity) });
      if (error) {
        refreshAllViews(queryClient);
      } else {
        patchAllViews(queryClient, (rows) => rows.filter((view) => view.id !== input.savedViewId));
      }
      // Dropped rather than invalidated: the row is gone, so a refetch would only
      // 404, and a cached copy left behind would keep resolving as the active view.
      queryClient.removeQueries({ queryKey: savedViewByIdQueryKey(input.savedViewId) });
    },
  });
}

export interface SavedViewAppearanceInput {
  /** The view's own entity. The backend still checks the entity permission on this update. */
  entity: string;
  savedViewId: number;
  /** An icon key from SAVED_VIEW_ICONS, or null for the bookmark. */
  icon: string | null;
  /** A colour key from SAVED_VIEW_COLORS, or null for lime. */
  color: string | null;
}

/**
 * Change only a view's icon and colour. The backend accepts this without the
 * name and definition, so it can never overwrite a rename or a save made in
 * another tab.
 *
 * Every cached copy of the row changes at once, so the sidebar, the breadcrumb
 * and Cmd+K all show the new icon before the server answers.
 */
export function useUpdateSavedViewAppearance() {
  const queryClient = useQueryClient();
  function applyAppearance(input: SavedViewAppearanceInput, icon: string | null, color: string | null): void {
    const rewrite = (rows: SavedViewRecord[]) =>
      rows.map((view) => (view.id === input.savedViewId ? { ...view, icon, color } : view));
    queryClient.setQueriesData<SavedViewPages>({ queryKey: savedViewsEntityKey(input.entity) }, (old) =>
      old ? { ...old, pages: old.pages.map((page) => ({ ...page, data: rewrite(page.data) })) } : old,
    );
    patchAllViews(queryClient, rewrite);
    queryClient.setQueryData<SavedViewRecord | null>(savedViewByIdQueryKey(input.savedViewId), (old) =>
      old ? { ...old, icon, color } : old,
    );
  }
  return useMutation({
    mutationFn: async (input: SavedViewAppearanceInput): Promise<SavedViewRecord> => {
      const response = await http.put<ItemEnvelope<SavedViewRecord>>(`${RESOURCE_BASE}/${input.savedViewId}`, {
        entity: input.entity,
        icon: input.icon,
        color: input.color,
      });
      const updated = response.data?.data;
      if (!updated) {
        throw new Error('Saved view update response is missing its data envelope.');
      }
      return updated;
    },
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: savedViewsEntityKey(input.entity) });
      const previous = flattenSavedViewPages(
        queryClient.getQueryData<SavedViewPages>(savedViewsQueryKey(ALL_ENTITIES, '')),
      ).find((view) => view.id === input.savedViewId);
      applyAppearance(input, input.icon, input.color);
      return { previous };
    },
    onError: (error, input, context) => {
      if (context?.previous) {
        applyAppearance(input, context.previous.icon ?? null, context.previous.color ?? null);
      } else {
        refreshAllViews(queryClient);
        queryClient.invalidateQueries({ queryKey: savedViewsEntityKey(input.entity) });
      }
      toast.error(serverRefusalMessage(error) ?? "Could not change the view's icon.");
    },
  });
}
